import React, { useState, useEffect } from 'react';
import {
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    Snackbar
} from '@mui/material';

const RealmAbilities = ({ characterData, updateCharacterData, classData }) => {
    const [pointsPerAbility, setPointsPerAbility] = useState(characterData.realmAbilities || {});
    const [raPoints, setRaPoints] = useState(characterData.raPoints || { total: 0, used: 0, remaining: 0 });
    const [hoveredAbility, setHoveredAbility] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const realmAbilities = classData?.['realm abilities'];

    useEffect(() => {
        if (!Array.isArray(realmAbilities)) return;
        calculateUsedPoints();
    }, [pointsPerAbility]);

    if (!Array.isArray(realmAbilities) || realmAbilities.length === 0) {
        return (
            <Box p={2}>
                <Typography variant="h6" color="error">
                    This class has no available Realm Abilities.
                </Typography>
            </Box>
        );
    }

    const calculateUsedPoints = () => {
        let used = 0;
        realmAbilities.forEach((a) => {
            const level = pointsPerAbility[a.name] || 0;
            for (let i = 0; i < level; i++) {
                used += a.levels[i].cost;
            }
        });
        const remaining = raPoints.total - used;
        const updated = { total: raPoints.total, used, remaining };
        setRaPoints(updated);
        updateCharacterData({ realmAbilities: pointsPerAbility, raPoints: updated });
    };

    const handleChange = (name, delta) => {
        const current = pointsPerAbility[name] || 0;
        const ability = realmAbilities.find((a) => a.name === name);
        const newLevel = current + delta;

        if (newLevel < 0 || newLevel > ability.levels.length) return;

        const costDelta = delta > 0
            ? ability.levels[newLevel - 1].cost
            : -ability.levels[newLevel]?.cost || 0;

        if (delta > 0 && costDelta > raPoints.remaining) {
            openSnackbar('Not enough realm ability points.');
            return;
        }

        const updated = { ...pointsPerAbility, [name]: newLevel };
        setPointsPerAbility(updated);
    };

    const openSnackbar = (message) => setSnackbarOpen({ open: true, message });
    const handleCloseSnackbar = () => setSnackbarOpen(false);

    const compactRowSx = { height: '32px', padding: '2px 8px' };
    const columnWidths = [
        { width: '30%' },
        { width: '15%' },
        { width: '15%' },
        { width: '25%' },
        { width: '15%' }
    ];

    return (
        <Grid container spacing={2} justifyContent="space-between" alignItems="flex-start">
            <Grid item xs={12} md={8}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...compactRowSx, ...columnWidths[0] }}>Name</TableCell>
                                <TableCell sx={{ ...compactRowSx, ...columnWidths[1] }}>Type</TableCell>
                                <TableCell sx={{ ...compactRowSx, ...columnWidths[2] }}>Reuse</TableCell>
                                <TableCell sx={{ ...compactRowSx, ...columnWidths[3] }}>Levels</TableCell>
                                <TableCell sx={{ ...compactRowSx, ...columnWidths[4] }}>Points</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {realmAbilities.map((ability) => {
                                const currentLevel = pointsPerAbility[ability.name] || 0;
                                return (
                                    <TableRow key={ability.name} sx={compactRowSx}>
                                        <TableCell sx={{ ...compactRowSx, ...columnWidths[0] }}>{ability.name}</TableCell>
                                        <TableCell sx={{ ...compactRowSx, ...columnWidths[1] }}>{ability.type}</TableCell>
                                        <TableCell sx={{ ...compactRowSx, ...columnWidths[2] }}>{ability.reuse || '-'}</TableCell>
                                        <TableCell sx={{ ...compactRowSx, ...columnWidths[3] }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {ability.levels.map((lvl, i) => (
                                                    <Button
                                                        key={i}
                                                        size="small"
                                                        sx={{
                                                            minWidth: '24px',
                                                            height: '24px',
                                                            padding: 0,
                                                            backgroundColor: currentLevel >= lvl.level ? 'primary.main' : '',
                                                            color: currentLevel >= lvl.level ? 'background.paper' : '',
                                                            ':hover': {
                                                                backgroundColor: currentLevel >= lvl.level ? 'primary.dark' : '',
                                                            },
                                                        }}
                                                        title={`[${lvl.level}] Value: ${lvl.value} (Cost: ${lvl.cost})`}
                                                        onMouseEnter={() => setHoveredAbility(lvl)}
                                                        onMouseLeave={() => setHoveredAbility(null)}
                                                    >
                                                        {lvl.level}
                                                    </Button>
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ ...compactRowSx, ...columnWidths[4] }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Button size="small" onClick={() => handleChange(ability.name, -1)} disabled={currentLevel <= 0}>-</Button>
                                                <Typography>{currentLevel}</Typography>
                                                <Button size="small" onClick={() => handleChange(ability.name, 1)} disabled={currentLevel >= ability.levels.length}>+</Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6">Realm Ability Points</Typography>
                    <Typography>Total: {raPoints.total}</Typography>
                    <Typography>Used: {raPoints.used}</Typography>
                    <Typography>Remaining: {raPoints.remaining}</Typography>
                </Paper>
                {hoveredAbility && (
                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="subtitle1">[{hoveredAbility.level}]</Typography>
                        <Typography>Value: {hoveredAbility.value}</Typography>
                        <Typography>Cost: {hoveredAbility.cost}</Typography>
                    </Paper>
                )}
            </Grid>
            <Snackbar
                open={snackbarOpen.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                message={snackbarOpen.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Grid>
    );
};

export default RealmAbilities;
